<template>
  <div class="mc-show-image" v-if="visible">
    <!-- Download and Close Button -->
    <div class="__top-button">
      <i
        v-if="allowDownload"
        @click="handleDownload"
        class="icon-outline_download prevent__close-preview"
      ></i>
      <i @click="handleClose" class="icon-outline_close"></i>
    </div>

    <!-- Preview Image -->
    <div class="__preview-container">
      <b-button
        v-if="attachments.length > 1"
        variant="outline-light"
        class="switch-img-btn previous-btn prevent__close-preview"
        :disabled="isFirstAttachment"
        @click="handleSwitchImage('previous')"
      >
        <i class="icon-outline_chevron-left prevent__close-preview"></i>
      </b-button>

      <div ref="img-ref" class="active-image">
        <img
          v-click-outside="vcoConfig"
          :style="{ transform }"
          :src="activeAttachmentUrl"
          :alt="activeAttachmentName"
        />
        <p>{{ activeAttachmentName }}</p>
      </div>

      <b-button
        v-if="attachments.length > 1"
        variant="outline-light"
        class="switch-img-btn after-btn prevent__close-preview"
        :disabled="isLastAttachment"
        @click="handleSwitchImage('after')"
      >
        <i class="icon-outline_chevron-right prevent__close-preview"></i>
      </b-button>
    </div>

    <!-- Operation Button -->
    <div class="__operation">
      <div class="operation-btn">
        <template v-for="operation in operations">
          <b-button
            variant="primary"
            :key="operation.key"
            :class="`${operation.key}-btn prevent__close-preview`"
            @click="handleTransformImage(operation.key)"
          >
            <i class="prevent__close-preview" :class="operation.icon"></i>
          </b-button>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'McShowImage',
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    attachments: {
      type: Array,
      default: () => [],
    },
    activeIndex: {
      type: Number,
      default: 0,
    },
    allowDownload: {
      type: Boolean,
      default: true,
    }
  },
  emits: ['update:visible', 'update:activeIndex', 'download'],
  data() {
    return {
      deg: 0,
      scale: 1,
      vcoConfig: {
        handler: this.handleClickOutsideImg,
        middleware: this.middlewareClickOutsideImg,
        events: ['dblclick', 'click'],
      },
    }
  },
  computed: {
    operations() {
      return [
        { key: 'maximize', icon: 'icon-outline_maximize' },
        { key: 'minimize', icon: 'icon-outline_minimize' },
        { key: 'rotate_left', icon: 'icon-outline_refresh' },
        { key: 'rotate_right', icon: 'icon-outline_refresh' },
      ]
    },
    activeAttachment() {
      return this.attachments[this.activeIndex] || {}
    },
    activeAttachmentUrl() {
      return this.activeAttachment.url || this.activeAttachment.file_url || ''
    },
    activeAttachmentName() {
      return this.activeAttachment.name || this.activeAttachment.file_name || `foto${this.activeIndex + 1}.jpg`
    },
    isFirstAttachment() {
      return this.activeIndex === 0
    },
    isLastAttachment() {
      return this.activeIndex === this.attachments.length - 1
    },
    transform() {
      return `scale(${this.scale}, ${this.scale}) rotate(${this.deg}deg)`
    },
  },
  mounted() {
    document.addEventListener('keydown', this.handleKeyDown)
  },
  beforeDestroy() {
    document.removeEventListener('keydown', this.handleKeyDown)
  },
  methods: {
    handleKeyDown(event) {
      if (this.visible && event.code === 'Escape') {
        this.handleClose()
      }
    },
    handleClickOutsideImg(_event) {
      this.handleClose()
    },
    middlewareClickOutsideImg(event) {
      return !event.target.className.includes('prevent__close-preview')
    },
    handleClose() {
      this.$emit('update:visible', false)
      this.deg = 0
      this.scale = 1
    },
    handleDownload() {
      this.$emit('download', this.activeAttachmentUrl, this.activeAttachmentName)
    },
    handleSwitchImage(key) {
      if (key === 'previous' && !this.isFirstAttachment) {
        this.$emit('update:activeIndex', this.activeIndex - 1)
      } else if (key === 'after' && !this.isLastAttachment) {
        this.$emit('update:activeIndex', this.activeIndex + 1)
      }
      this.deg = 0
      this.scale = 1
    },
    handleTransformImage(key) {
      switch (key) {
        case 'maximize':
          if (this.scale < 1.5) this.scale += 0.5
          break
        case 'minimize':
          if (this.scale > 0.5) this.scale -= 0.25
          break
        case 'rotate_left':
          this.deg -= 90
          break
        case 'rotate_right':
          this.deg += 90
          break
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.mc-show-image {
  position: fixed;
  z-index: 9999;
  left: 0;
  top: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(1, 19, 61, 0.6);
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  .__top-button {
    display: flex;
    justify-content: flex-end;
    gap: 20px;
    margin: 20px 28px;
    position: relative;
    z-index: 10;
    i {
      cursor: pointer;
      font-size: 24px;
      color: #ffffff;
      &:hover { opacity: 0.8; }
    }
  }

  .__preview-container {
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 0 40px;
    flex-grow: 1;
    position: relative;
    overflow: hidden; // Memotong gambar yang di-zoom agar tidak menutupi tombol

    .switch-img-btn {
      border: none;
      background: transparent;
      position: absolute;
      top: 50%;
      transform: translateY(-50%);
      z-index: 2;

      &.previous-btn {
        left: 20px;
      }

      &.after-btn {
        right: 20px;
      }

      i::before {
        font-size: 48px;
        color: #ffffff;
      }
      &:hover { background-color: rgba(255, 255, 255, 0.1); }
      &:disabled { opacity: 0.2; cursor: default; }
    }

    .active-image {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      padding: 20px 0;
      color: #ffffff;
      height: 100%;
      width: 100%;

      img {
        max-width: 100%;
        max-height: calc(100vh - 300px); // Menambah batas agar ada ruang untuk gap
        box-shadow: 0px 15px 50px 0px rgba(0, 0, 0, 0.5);
        transition: transform 0.3s ease-out;
        object-fit: contain;
      }
      p { 
        margin-top: 20px; 
        margin-bottom: 0;
        font-weight: 500; 
        text-shadow: 0 2px 4px rgba(0,0,0,0.5);
      }
    }
  }

  .__operation {
    display: flex;
    justify-content: center;
    margin-top: 20px; // Tambahkan jarak antara gambar dan tombol kontrol
    margin-bottom: 40px;
    position: relative;
    z-index: 10;
    .operation-btn {
      display: flex;
      gap: 8px;
      background: #0d4491; // Dark blue from screenshot
      padding: 6px 6px;
      border-radius: 6px; // Rounded but more rectangular like a pill/bar
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);

      button {
        background: transparent !important;
        border: none !important;
        width: 32px;
        height: 32px;
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 0;
        transition: opacity 0.2s;

        i {
          font-size: 22px;
          color: #ffffff;
        }

        &:hover {
          opacity: 0.7;
        }

        &:active {
          transform: scale(0.95);
        }

        &.rotate_left-btn {
          i {
            transform: scaleX(-1);
          }
        }
      }
    }
  }
}
</style>
